import type { ElementsState } from "@v1/types/core-state";
import type {
  ComponentErrorContextValue,
  ComponentInventoryContextValue,
  ComponentUpdateContextValue,
  CustomFontsContextValue,
  DraftElementContextValue,
  DynamicDataStore,
  DynamicDataStoreContextValue,
  ExtraContextValue,
  FeatureFlagsContextValue,
  RenderEnvironmentContextValue,
  ReploEditorActiveCanvasContextValue,
  ReploEditorCanvasContextValue,
  ReploSymbolsContextValue,
  RuntimeHooksContextValue,
  ShopifyStoreContextValue,
  SyncRuntimeStateContextValue,
} from "replo-runtime/shared/runtime-context";
import type { SharedStatePayload } from "replo-runtime/shared/types";
import type { EditorCanvas } from "replo-utils/lib/misc/canvas";

import * as React from "react";

import { skipToken } from "@tanstack/react-query";
import {
  selectActiveCanvas,
  selectCanvasInteractionMode,
  setCanvasInteractionMode,
} from "@v1/components/canvas/canvas-reducer";
import {
  useDesignLibrary,
  useEditorOverrideOpenModalId,
  useEditorOverrideText,
  useIsBrokenEditorMediaId,
  useIsLabelledByOtherComponent,
  useIsPreviewMode,
  useIsShopifyProductsLoading,
  useIsUploadingEditorMediaId,
  usePreviewWidth,
  useRuntimeOverrideVariantId,
  useSharedState,
  useShopifyProductMetafieldValues,
  useShopifyProducts,
  useShopifyVariantMetafieldValues,
  useSwatches,
  useTemplateEditorProduct,
} from "@v1/components/canvas/stores/runtime";
import useApplyComponentAction from "@v1/hooks/useApplyComponentAction";
import useGetStoreNameAndUrl from "@v1/hooks/useGetStoreNameAndUrl";
import { useSyncRuntime } from "@v1/hooks/useSyncRuntimeValue";
import {
  selectLocaleData,
  selectOkendoWidgetNamespace,
  selectShopifyUrlRoot,
} from "@v1/reducers/commerce-reducer";
import {
  selectDraftComponentId,
  selectDraftElementType,
  selectDraftNodes,
} from "@v1/reducers/core-reducer";
import { addEditorBrokenMediaComponentId } from "@v1/reducers/editor-media-reducer";
import { setSharedState } from "@v1/reducers/paint-reducer";
import { enhanceElementMapping } from "@v1/reducers/utils/core-reducer-utils";
import { useEditorDispatch, useEditorSelector } from "@v1/store/store";
import { storeRouter } from "@v1/trpc/routes/store";
import { trackError } from "@v2/features/infra/analytics/tracking";
import { isFeatureEnabled } from "@v2/features/infra/featureFlags";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useOpenModal } from "@v2/store/modalStore";
import { elementUtils } from "@v2/trpc/routes/element";
import { isDevelopment } from "@v2/utils/env";
import { shallowEqual } from "react-redux";
import { componentInventory } from "replo-runtime/store/componentInventory";
import {
  fakeProducts,
  loadingProduct,
} from "replo-runtime/store/utils/fakeProducts";
import { canUseDOM } from "replo-utils/dom/misc";

// NOTE (Chance 2024-03-13): The following hooks are used to construct the
// context values that will be passed into all of the providers needed for
// runtime components in the editor. Each environment will have its own set of
// functions to serve a similar function, since the data sources for each
// environment are different.
//
// Also note that these all liberally use `useMemo` to return stable objects. We
// should be able to remove and simplify some of these, but I opted to keep them
// consistent for now since the code is very much in-flux and returned objects
// may change throughout the larger refactor process.

export function useInitComponentErrorContext() {
  const openModal = useOpenModal();
  return React.useMemo<ComponentErrorContextValue>(() => {
    return {
      onComponentError: (componentId, error, reactErrorInfo) => {
        if (isDevelopment) {
          console.error("[REPLO] Component rendering error.", {
            componentId,
            error,
            reactErrorInfo,
          });
        }
        trackError(error);
        openModal({
          type: "fullPageErrorModal",
          props: {
            details: {
              header: "Unable to render content",
              subheader: "Something went wrong displaying this content.",
              message:
                "This usually indicates an internal error. If this error persists, please contact support@replo.app.",
            },
          },
        });
      },
    };
  }, [openModal]);
}

export function useInitComponentInventoryContext() {
  return React.useMemo<ComponentInventoryContextValue>(() => {
    return { componentInventory };
  }, []);
}

export function useInitComponentUpdateContext() {
  const applyComponentAction = useApplyComponentAction();
  const dispatch = useEditorDispatch();

  type OnSubmit =
    ComponentUpdateContextValue["onSubmitContentEditableTextUpdate"];

  const setContentEditing = React.useCallback(
    (contentEditing: boolean) => {
      dispatch(
        setCanvasInteractionMode(contentEditing ? "content-editing" : "edit"),
      );
    },
    [dispatch],
  );

  const onSubmitContentEditableTextUpdate = React.useCallback<OnSubmit>(
    (componentId, htmlContent) => {
      setContentEditing(false);
      const textContent = htmlContent
        .replace(/<[^>]*>/g, "")
        .replace(/&nbsp;/g, "");
      const isEmpty = textContent.trim() === "";

      // Note (Ben O., 2025-05-08): if the text content is empty, we should delete the component.
      if (isEmpty) {
        applyComponentAction({
          type: "deleteComponent",
          componentId,
          source: "content-editable",
        });
      } else {
        applyComponentAction({
          type: "setProps",
          componentId,
          value: {
            text: htmlContent,
          },
        });
      }
    },
    [applyComponentAction, setContentEditing],
  );

  return React.useMemo<ComponentUpdateContextValue>(() => {
    return {
      onSubmitContentEditableTextUpdate,
    };
  }, [onSubmitContentEditableTextUpdate]);
}

export function useInitCustomFontsContext() {
  const projectId = useCurrentProjectId();
  const { data: uploadedFonts } = storeRouter.getFonts.useQuery(
    projectId ? { storeId: projectId } : skipToken,
  );
  return React.useMemo<CustomFontsContextValue>(() => {
    return { uploadedFonts: uploadedFonts ?? [] };
  }, [uploadedFonts]);
}

function getEnhancedElementMapping(
  elements: ElementsState,
  projectId?: string,
): ElementsState {
  if (
    !elements.selectedRevisionId &&
    !elements.streamingUpdate &&
    !elements.selectedArchivedElementId
  ) {
    return elements;
  }

  const archivedElements = projectId
    ? elementUtils.listArchived.getData({ projectId })
    : [];

  const {
    draftElementId,
    elementRevisions,
    selectedRevisionId,
    mapping: elementMapping,
    selectedArchivedElementId,
    streamingUpdate,
  } = elements;

  const newMapping = enhanceElementMapping({
    draftElementId,
    elementRevisions,
    selectedRevisionId,
    elementMapping,
    streamingDraftElementComponent: streamingUpdate?.draftElementComponent,
    selectedArchivedElementId,
    archivedElements,
  });

  return {
    ...elements,
    mapping: newMapping,
  };
}

export function useInitDraftElementContext() {
  const projectId = useCurrentProjectId();
  const ctx: DraftElementContextValue = useEditorSelector((state) => {
    const editorMode = state.core.editorMode;
    const elements = getEnhancedElementMapping(state.core.elements, projectId);
    const draftElementId = elements.draftElementId ?? null;
    const draftComponentId = elements.draftComponentIds[0] ?? null;
    const { draftSymbolInstanceId, draftSymbolId } = elements;
    return {
      draftElementId:
        editorMode === "archived" && elements.selectedArchivedElementId
          ? elements.selectedArchivedElementId
          : draftElementId,
      draftSymbolInstanceId,
      draftSymbolId,
      draftComponentId,
    };
  }, shallowEqual);
  return ctx;
}

export function useInitDynamicDataStoreContext() {
  const [store, setStore] = React.useState<DynamicDataStore>({});
  return React.useMemo<DynamicDataStoreContextValue>(() => {
    return { store, setStore };
  }, [store]);
}

export function useInitShopifyStoreContext() {
  const {
    storeId,
    activeCurrency,
    activeLanguage,
    activeShopifyUrlRoot,
    moneyFormat,
    moneyWithCurrencyFormat,
  } = useEditorSelector((state) => {
    const {
      activeCurrency,
      activeLanguage,
      moneyFormat,
      moneyWithCurrencyFormat,
    } = selectLocaleData(state);
    const activeShopifyUrlRoot = selectShopifyUrlRoot(state);
    return {
      storeId: state.core.project?.id,
      activeCurrency,
      activeLanguage,
      activeShopifyUrlRoot,
      moneyFormat,
      moneyWithCurrencyFormat,
    };
  }, shallowEqual);
  const { storeUrl } = useGetStoreNameAndUrl();

  return React.useMemo<ShopifyStoreContextValue>(() => {
    return {
      storeId,
      storeUrl,
      templateProduct: null,
      fakeProducts,
      loadingProduct,
      activeCurrency,
      activeLanguage,
      moneyFormat,
      moneyWithCurrencyFormat,
      activeShopifyUrlRoot,
    };
  }, [
    storeId,
    storeUrl,
    activeCurrency,
    activeLanguage,
    moneyFormat,
    moneyWithCurrencyFormat,
    activeShopifyUrlRoot,
  ]);
}

export function useInitExtraContext() {
  const okendoNamespace = useEditorSelector(selectOkendoWidgetNamespace);
  const ctx: ExtraContextValue = React.useMemo<ExtraContextValue>(() => {
    return {
      disableLiquid: false,
      runtimeVersion: null,
      okendoNamespace,
    };
  }, [okendoNamespace]);
  return ctx;
}

export function useInitFeatureFlagsContext() {
  const carouselDebug = isFeatureEnabled("carousel-debug");
  return React.useMemo<FeatureFlagsContextValue>(() => {
    return {
      featureFlags: {
        carouselDebug,
      },
    };
  }, [carouselDebug]);
}

type EditorRenderEnvironmentContextValue = RenderEnvironmentContextValue & {
  isEditorApp: true;
};

export function useInitRenderEnvironmentContext() {
  return React.useMemo<EditorRenderEnvironmentContextValue>(() => {
    return {
      isEditorApp: true,
      isElementPreview: false,
      isPublishedPage: false,
      isPublishing: false,
      previewEnvironment: "editor",
      renderEnvironment: "editor",
    };
  }, []);
}

export function useInitReploEditorActiveCanvasContext() {
  const activeCanvas = useEditorSelector(selectActiveCanvas);
  const ctx: ReploEditorActiveCanvasContextValue = React.useMemo(() => {
    return { activeCanvas };
  }, [activeCanvas]);
  return ctx;
}

export function useInitReploEditorCanvasContext() {
  // TODO (Chance 2024-03-14): Stop doing this and get values from the store.
  // Keeping for back-compat right now. This may cause problems when we remove
  // Repainter since this is not stateful.
  const editorCanvasScale = canUseDOM
    ? window.alchemyEditor?.canvasScale ?? 0
    : 0;
  const editorCanvasYOffset = canUseDOM
    ? window.alchemyEditor?.canvasYOffset ?? 0
    : 0;

  return React.useMemo<ReploEditorCanvasContextValue>(() => {
    return {
      editorCanvasYOffset,
      editorCanvasScale,
    };
  }, [editorCanvasYOffset, editorCanvasScale]);
}

export function useInitReploElementContext() {
  const draftElementType = useEditorSelector(selectDraftElementType);
  return React.useMemo(
    () => ({
      elementType: draftElementType,
      useSectionSettings: undefined,
    }),
    [draftElementType],
  );
}

export function useInitReploSymbolsContext() {
  const symbols = useEditorSelector(
    (state) => Object.values(state.core.symbols.mapping),
    shallowEqual,
  );
  return React.useMemo<ReploSymbolsContextValue>(() => {
    return { symbols };
  }, [symbols]);
}

export function useInitSyncRuntimeStateContext() {
  const { setSyncRuntimeState } = useSyncRuntime();
  const ctx = React.useMemo<SyncRuntimeStateContextValue>(() => {
    return {
      setSyncRuntimeState,
    };
  }, [setSyncRuntimeState]);
  return ctx;
}

const useSetEditorBrokenMediaComponentId = () => {
  const dispatch = useEditorDispatch();
  return React.useCallback(
    (componentId: string) => {
      return dispatch(addEditorBrokenMediaComponentId(componentId));
    },
    [dispatch],
  );
};

export function useInitRuntimeHooksContext() {
  return React.useMemo<RuntimeHooksContextValue>(() => {
    return {
      usePreviewWidth() {
        return usePreviewWidth();
      },
      useEditorOverrideActiveVariantId(componentId: string) {
        return useRuntimeOverrideVariantId(componentId) ?? null;
      },
      useEditorOverrideTextValue(componentId: string) {
        return useEditorOverrideText(componentId) ?? null;
      },
      useIsEditorEditModeRenderEnvironment() {
        const isPreviewMode = useIsPreviewMode();
        return !isPreviewMode;
      },
      useIsLabelledByOtherComponent(componentId: string) {
        return useIsLabelledByOtherComponent(componentId);
      },
      useSharedState(key: string) {
        return useSharedState(key);
      },
      useSetSharedState() {
        const dispatch = useEditorDispatch();
        return React.useCallback(
          (setSharedStatePayload: SharedStatePayload) => {
            return dispatch(setSharedState(setSharedStatePayload));
          },
          [dispatch],
        );
      },
      useSwatches() {
        return useSwatches();
      },
      useTemplateEditorProduct() {
        return useTemplateEditorProduct();
      },
      useShopifyProducts() {
        return useShopifyProducts();
      },
      useIsShopifyProductsLoading() {
        return useIsShopifyProductsLoading();
      },
      useShopifyProductMetafieldValues() {
        return useShopifyProductMetafieldValues();
      },
      useShopifyVariantMetafieldValues() {
        return useShopifyVariantMetafieldValues();
      },
      useIsContentEditing({
        componentId,
        canvas,
        element,
      }: {
        componentId: string;
        canvas: EditorCanvas;
        element: HTMLElement;
      }) {
        const draftComponentId = useEditorSelector(selectDraftComponentId);
        const draftNodes = useEditorSelector(selectDraftNodes);

        const activeCanvas = useEditorSelector(selectActiveCanvas);
        const isContentEditing =
          useEditorSelector(selectCanvasInteractionMode) === "content-editing";
        if (!isContentEditing) {
          return false;
        }

        if (draftComponentId !== componentId || canvas !== activeCanvas) {
          return false;
        }

        if (draftNodes.length > 0 && !draftNodes.includes(element)) {
          return false;
        }

        return true;
      },
      useIsUploadingEditorMediaId(componentId: string) {
        return useIsUploadingEditorMediaId(componentId);
      },
      useIsBrokenEditorMediaId(componentId: string) {
        return useIsBrokenEditorMediaId(componentId);
      },
      useSetEditorBrokenMediaComponentId() {
        return useSetEditorBrokenMediaComponentId();
      },
      useEditorOverrideOpenModalId() {
        return useEditorOverrideOpenModalId();
      },
      useDesignLibrary() {
        return useDesignLibrary();
      },
    };
  }, []);
}
